import React, { useId } from 'react';
import { Flex } from 'glints-aries/es';
import { Icon, OutlineButton, Typography } from 'glints-aries/es/@next';
import { borderRadius4 } from 'glints-aries/es/@next/utilities/borderRadius';
import { Blue, Neutral, Red } from 'glints-aries/es/@next/utilities/colors';
import { space8, space16 } from 'glints-aries/es/@next/utilities/spacing';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import truncateFileName from '../../../../common/truncate';
import { useIsViewOnlyMode } from '../FormContext';

type TextFieldProps = {
  file: {
    assetURL: string;
    assetFileName: string | undefined;
    disablePreview?: boolean;
  };
  handleRemoveFile: () => void;
  removeFileDisabled?: boolean;
  showReupload?: boolean;
  acceptedFileType?: string[];
  handleReupload?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const FileItemContainer = styled.div<{ $isError: boolean }>`
  display: flex;
  align-items: center;
  border: 1px solid ${({ $isError }) => ($isError ? Red.B93 : Neutral.B85)};
  border-radius: ${borderRadius4};
  flex: 1;
  width: 100%;
  padding: ${space16} 20px;
`;

const RemoveFileIcon = styled(Icon).attrs({
  name: 'ri-delete-bin-line',
})`
  cursor: pointer;
  fill: ${Neutral.B40};

  &[data-disabled='true'] {
    cursor: default;
    pointer-events: none;
    fill: ${Neutral.B85};
  }
`;

const ReuploadButton = styled(OutlineButton)`
  color: ${Blue.S99};
  border: 1px solid ${Blue.S99};
`;

export default function FileItem({
  file,
  handleRemoveFile,
  removeFileDisabled,
  showReupload,
  acceptedFileType,
  handleReupload,
}: TextFieldProps) {
  const isViewOnly = useIsViewOnlyMode();
  const inputElemId = useId();

  return (
    <FileItemContainer $isError={Boolean(showReupload)}>
      <a
        href={file.assetURL}
        target="_blank"
        rel="noreferrer"
        style={{
          flex: 1,
          pointerEvents: file.disablePreview ? 'none' : 'auto',
        }}
      >
        <Flex alignItems="center" gap={space8}>
          <Icon name="ri-file-line" width={24} height={24} fill={Neutral.B40} />
          <Typography
            variant="subtitle2"
            color={isViewOnly ? Blue.S99 : Neutral.B18}
          >
            {truncateFileName(file.assetFileName || '', 50)}
          </Typography>
        </Flex>
      </a>
      <Choose>
        <When condition={!showReupload}>
          <RemoveFileIcon
            onClick={handleRemoveFile}
            width={24}
            height={24}
            data-disabled={isViewOnly || removeFileDisabled}
          />
        </When>
        <Otherwise>
          <input
            type="file"
            id={inputElemId}
            accept={acceptedFileType?.join(', ')}
            hidden={true}
            onChange={e => handleReupload?.(e)}
          />
          <label htmlFor={inputElemId}>
            <ReuploadButton
              onClick={e => e.currentTarget.parentElement?.click()}
            >
              <Typography variant="button">
                <FormattedMessage
                  id="interactive-reupload"
                  defaultMessage="Reupload"
                />
              </Typography>
            </ReuploadButton>
          </label>
        </Otherwise>
      </Choose>
    </FileItemContainer>
  );
}
